import { useContextBridge } from '@react-three/drei'
import { Canvas, invalidate } from '@react-three/fiber'
import { SceneContext } from 'providers/SceneProvider'
import { FC, useRef, useState } from 'react'
import { useAsync } from 'react-use'
import { Matrix4, PerspectiveCamera, Raycaster, Scene as ThreeScene, Vector3, WebGLRenderer } from 'three'

import { startPosition } from 'utils/getData'
import { initMap } from 'utils/initMap'
import Slides from './Slides'

interface Props {}
export interface LatLngLiteral {
  lat: number
  lng: number
}

const Scene: FC<Props> = () => {
  const SceneContextBridge = useContextBridge(SceneContext)
  const element = useRef<HTMLDivElement>(null)
  const canvas = useRef<HTMLCanvasElement>(null)
  const renderer = useRef<WebGLRenderer>()
  const scene = useRef<ThreeScene>(new ThreeScene())
  const camera = useRef<PerspectiveCamera>(new PerspectiveCamera())
  const map = useRef<google.maps.Map>(null!)
  const raycaster = useRef<Raycaster>(new Raycaster(new Vector3(13, 37, 0)))
  const [initialized, setInitialized] = useState(false)

  useAsync(async () => {
    if (!element.current) return
    if (!initialized) {
      map.current = await initMap({ element: element.current, startPosition })
      const overlay = new (google.maps as any).WebGLOverlayView()

      overlay.onAdd = () => {}

      overlay.onDraw = ({ gl, transformer }: { gl: WebGLRenderingContext; transformer: any }) => {
        overlay.requestRedraw()
        const latLngAltitudeLiteral = {
          lat: startPosition.lat ?? 0,
          lng: startPosition.lng ?? 0,
          altitude: 0,
        }

        const matrix = transformer.fromLatLngAltitude(latLngAltitudeLiteral)
        camera.current.projectionMatrix = new Matrix4().fromArray(matrix)

        const cameraParams = transformer.getCameraParams()
        const cameraPositionMatrix = transformer.fromLatLngAltitude({ lat: cameraParams.lat ?? 0, lng: cameraParams.lng ?? 0, altitude: 0 })
        camera.current.position.setFromMatrixPosition(new Matrix4().fromArray(cameraPositionMatrix))

        if (renderer.current) {
          // Does not seem to do anything
          const { width, height } = gl.canvas
          renderer.current.setViewport(0, 0, width, height)
          // ===============================

          renderer.current.render(scene.current, camera.current) // reset render in map
          renderer.current.resetState() // reset render in map
          invalidate() // reset render in canvas
        }
        overlay.requestRedraw()
      }

      overlay.onContextRestored = ({ gl }: { gl: WebGLRenderingContext }) => {
        renderer.current = new WebGLRenderer({
          canvas: gl.canvas,
          context: gl,
          ...gl.getContextAttributes(),
        })

        renderer.current.autoClear = false
      }

      setInitialized(true)

      overlay.setMap(map.current)
    }
  }, [scene.current, element])

  return (
    <div>
      {/* <NavigateInstructionsOverlay hideOverlay={hideOverlay} /> */}
      <div ref={element} style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 10, opacity: 1 }} />
      <Canvas
        style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 5, background: 'white' }}
        frameloop='demand'
        ref={canvas}
        gl={renderer.current as any}
        raycaster={raycaster.current}
        camera={camera.current}
        onCreated={({ events }) => {
          events.connect?.(element.current)
        }}
      >
        <SceneContextBridge>
          <primitive object={scene.current}>
            <primitive object={camera.current}>
              <Slides map={map.current} />
            </primitive>
            <ambientLight args={[0xffffff]} intensity={3.5} />
          </primitive>
        </SceneContextBridge>
      </Canvas>
    </div>
  )
}

export default Scene
